import { Wrench, Droplets, CheckCircle, Phone, Star, Shield, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const Plumbing = () => {
  const services = [
    {
      title: 'Leak Detection & Repair',
      description: 'Fast, accurate leak detection and repair to prevent water damage and save you money on your water bill.',
      icon: Droplets,
    },
    {
      title: 'Drain Cleaning',
      description: 'Professional drain cleaning services to clear clogs and keep your pipes flowing smoothly.',
      icon: Wrench,
    },
    {
      title: 'Water Heater Services',
      description: 'Installation, repair, and maintenance of traditional and tankless water heaters.',
      icon: Wrench,
    },
    {
      title: 'Fixture Installation',
      description: 'Expert installation of faucets, sinks, toilets, and other plumbing fixtures.',
      icon: Wrench,
    },
    {
      title: 'Sewer Line Services',
      description: 'Sewer line repair, replacement, and camera inspection services.',
      icon: Wrench,
    },
    {
      title: 'Repiping',
      description: 'Whole-home repiping services using high-quality materials.',
      icon: Wrench,
    },
  ];

  const whyChooseUs = [
    'Licensed and insured Alabama plumbers',
    'Mon-Fri 8AM-5PM',
    'Upfront, honest pricing',
    '30-day workmanship guarantee',
    '65+ years combined experience',
    'Family-owned and operated',
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'Plumbing',
    'name': 'Plumbing Services in Dothan, AL',
    'description': 'Professional plumbing services including leak repair, drain cleaning, water heater installation, sewer line work, and repiping throughout the Wiregrass area.',
    'provider': { '@type': 'LocalBusiness', 'name': "Ard's Plumbing & Electric", 'telephone': '+1-334-692-5537', 'url': 'https://ardsdothan.com' },
    'areaServed': { '@type': 'AdministrativeArea', 'name': 'Wiregrass Area, Alabama' },
    'url': 'https://ardsdothan.com/services/plumbing',
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Plumbing Services',
      'itemListElement': [
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Leak Detection & Repair' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Drain Cleaning' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Water Heater Services' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Fixture Installation' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Sewer Line Services' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Repiping' } },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      { '@type': 'Question', 'name': 'How quickly can you respond to a plumbing problem?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'We offer Mon-Fri 8AM-5PM service with same-day appointments available in Dothan and the Wiregrass. Call (334) 692-5537.' } },
      { '@type': 'Question', 'name': 'Do you provide free plumbing estimates?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, we provide upfront pricing before any work begins — no hidden fees or surprises.' } },
      { '@type': 'Question', 'name': 'Are your plumbers licensed in Alabama?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, all our plumbers are fully licensed and insured in Alabama with 65+ years of combined experience.' } },
      { '@type': 'Question', 'name': 'What plumbing services do you offer in Dothan?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'We provide leak detection and repair, drain cleaning, water heater services, fixture installation, sewer line services, and whole-home repiping throughout the Wiregrass area.' } },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Services', 'item': 'https://ardsdothan.com/services' },
      { '@type': 'ListItem', 'position': 3, 'name': 'Plumbing', 'item': 'https://ardsdothan.com/services/plumbing' },
    ],
  };

  const faqs = [
    { q: 'How quickly can you respond to a plumbing problem?', a: 'We offer Mon-Fri 8AM-5PM service with same-day appointments available in Dothan and the Wiregrass. Call (334) 692-5537.' },
    { q: 'Do you provide free plumbing estimates?', a: 'Yes, we provide upfront pricing before any work begins — no hidden fees or surprises.' },
    { q: 'Are your plumbers licensed in Alabama?', a: 'Yes, all our plumbers are fully licensed and insured in Alabama with 65+ years of combined experience.' },
    { q: 'What plumbing services do you offer in Dothan?', a: 'We provide leak detection and repair, drain cleaning, water heater services, fixture installation, sewer line services, and whole-home repiping throughout the Wiregrass area.' },
  ];

  return (
    <>
      <SEO
        title="Plumbing Services Dothan AL | Ard's Plumbing & Electric"
        description="Professional plumbing services in Dothan, AL. Leak repair, drain cleaning, water heaters, sewer lines & more. Mon-Fri 8AM-5PM. Call (334) 692-5537."
        keywords="plumber Dothan AL, plumbing repair, drain cleaning, water heater repair, leak detection, sewer line repair, emergency plumber Wiregrass"
        canonical="/services/plumbing"
        additionalSchema={[serviceSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full mb-6">
                <Wrench className="w-4 h-4" />
                Professional Plumbing Services
              </div>
              <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
                PLUMBING SERVICES<br />
                <span className="text-brand-yellow">YOU CAN TRUST</span>
              </h1>
              <p className="text-lg text-white/90 mb-8 max-w-lg">
                From leaky faucets to major pipe repairs, our experienced plumbers handle it all. 
                Fast, reliable service in Dothan and the Wiregrass area.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
                >
                  <Link to="/contact-us">Schedule Service</Link>
                </Button>
                <a
                  href="tel:3346925537"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-blue transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  (334) 692-5537
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/plumbing-service.jpg"
                alt="Professional plumbing service in Dothan AL"
                className="rounded-[28px] shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand-blue rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-bold text-brand-black">4+ Rating</p>
                    <p className="text-sm text-gray-500">100+ Reviews</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              OUR PLUMBING SERVICES
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive plumbing solutions for homes and businesses throughout the Wiregrass area.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 bg-brand-blue/10 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="w-7 h-7 text-brand-blue" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-black mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-brand-navy">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
                WHY CHOOSE ARD'S<br />
                <span className="text-brand-yellow">FOR PLUMBING?</span>
              </h2>
              <p className="text-lg text-white/80 mb-8">
                When you need a plumber in Dothan or the surrounding Wiregrass area, 
                you need someone you can trust. Here's why homeowners and businesses choose us:
              </p>
              <ul className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Shield className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Licensed</p>
                <p className="text-white/70 text-sm">& Insured</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Clock className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Same-Day</p>
                <p className="text-white/70 text-sm">Service</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Star className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">5-Star</p>
                <p className="text-white/70 text-sm">Rated</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Wrench className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">65+ Years</p>
                <p className="text-white/70 text-sm">Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              FREQUENTLY ASKED QUESTIONS
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-lg text-brand-black mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-brand-coral">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
            NEED A PLUMBER NOW?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Don't let plumbing problems disrupt your day. Contact Ard's Plumbing & Electric 
            for fast, reliable service in Dothan and the Wiregrass area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-white text-brand-coral hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact-us">Book Online</Link>
            </Button>
            <a
              href="tel:3346925537"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-coral transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call (334) 692-5537
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Plumbing;
